import axios from "axios";
import { pushToast } from "../ui/toast";

const api = axios.create({
  baseURL: "/api",
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

// When a session expires (or was never established), the backend returns 401.
// Without a global handler, any api.* call lacking a local .catch() surfaces as
// an uncaught runtime error in dev. Treat a 401 as "session gone" and send the
// user to the login screen — except for the login request itself, which
// Login.tsx handles to show an inline "invalid credentials" message.
//
// For genuinely unexpected failures — server errors (5xx) and network errors —
// we also surface a toast, since most dashboard fetches swallow rejections with
// .catch(() => {}). We deliberately do NOT toast on other 4xx (404/400/403/409):
// several callers expect those (e.g. a missing certification 404s) and handle
// them, so toasting every non-401 would spam false errors.
api.interceptors.response.use(
  res => res,
  error => {
    const status: number | undefined = error?.response?.status;
    const url: string = error?.config?.url ?? "";
    const isLoginAttempt = url.includes("/auth/login");
    const isCanceled = error?.code === "ERR_CANCELED";

    if (status === 401 && !isLoginAttempt && window.location.pathname !== "/login") {
      window.location.assign("/login");
    } else if (!isLoginAttempt && !isCanceled) {
      if (status === undefined) {
        pushToast("Network error — couldn't reach the server.");
      } else if (status >= 500) {
        const serverMsg = error?.response?.data?.error;
        pushToast(serverMsg || `Server error (${status}). Please try again.`);
      }
    }
    return Promise.reject(error);
  }
);

// Auth
export const login = (username: string, password: string) =>
  api.post("/auth/login", { username, password });

export const logout = () => api.post("/auth/logout");

export const getCurrentUser = () => api.get("/auth/user");
// Whether this server is the hosted demonstration site (no login needed).
export const getDemoInfo = () => api.get("/demo-info");

export const changePassword = (current_password: string, new_password: string) =>
  api.post("/auth/change-password", { current_password, new_password });

// Admin: issue a one-time temp password for a user; returns { temp_password }.
export const resetUserPassword = (userId: number) =>
  api.post(`/admin/users/${userId}/reset-password`);

// Companies
export const getCompanies = (includeInactive = false) =>
  api.get("/admin/companies", { params: includeInactive ? { include_inactive: true } : {} });
export const createCompany = (data: object) => api.post("/admin/companies", data);
export const updateCompany = (id: number, data: object) => api.put(`/admin/companies/${id}`, data);
export const getArchivedCompanies = () => api.get("/admin/companies", { params: { archived: true } });
export const getCompanyDependents = (id: number) => api.get(`/admin/companies/${id}/dependents`);
// DELETE now archives (soft-delete) — records are retained, never destroyed.
export const archiveCompany = (id: number) => api.delete(`/admin/companies/${id}`);
export const restoreCompany = (id: number) => api.post(`/admin/companies/${id}/restore`);

// Permits
export const getPermits = () => api.get("/permits");
export const getPermit = (id: number) => api.get(`/permits/${id}`);
export const getExpiringPermits = () => api.get("/permits/expiring");
export const getCategoricalStandards = () => api.get("/permits/categorical-standards");
export const generatePermit = (data: object) => api.post("/permits/generate", data);
export const submitPermit = (id: number) => api.post(`/permits/${id}/submit`);
// Cancel a scheduled (future-dated) replacement; the permit returns to pending.
export const unschedulePermit = (id: number) => api.post(`/permits/${id}/unschedule`);
export const activatePermit = (id: number, supersedePermitId?: number, effectiveDate?: string) =>
  api.post(`/permits/${id}/activate`, {
    ...(supersedePermitId ? { supersede_permit_id: supersedePermitId } : {}),
    ...(effectiveDate ? { effective_date: effectiveDate } : {}),
  });
export const createPermit = (data: object) => api.post("/permits", data);
export const updatePermit = (id: number, data: object) => api.put(`/permits/${id}`, data);
export const addPermitLimit = (permitId: number, data: object) =>
  api.post(`/permits/${permitId}/limits`, data);
export const addPermitLimitsBatch = (permitId: number, data: object[], upsert = false) =>
  api.post(`/permits/${permitId}/limits/batch${upsert ? "?upsert=true" : ""}`, data);
export const deletePermitLimit = (permitId: number, limitId: number) =>
  api.delete(`/permits/${permitId}/limits/${limitId}`);
// A limit with results is retired (kept with its history) instead of deleted.
export const retirePermitLimit = (permitId: number, limitId: number) =>
  api.post(`/permits/${permitId}/limits/${limitId}/retire`);
export const restorePermitLimit = (permitId: number, limitId: number) =>
  api.post(`/permits/${permitId}/limits/${limitId}/restore`);

// Samples
export const submitSample = (data: object) => api.post("/samples", data);
export const getSamples = (companyId?: number, year?: number) =>
  api.get("/samples", { params: { company_id: companyId, year } });
export const getSampleYears = (companyId?: number) =>
  api.get("/samples/years", { params: { company_id: companyId } });
export const getSample = (id: number) => api.get(`/samples/${id}`);
export const deleteSample = (id: number) => api.delete(`/samples/${id}`);
export const getSampleCorrections = (id: number) => api.get(`/samples/${id}/corrections`);
export const deleteSampleResult = (sampleId: number, resultId: number) =>
  api.delete(`/samples/${sampleId}/results/${resultId}`);
export const addSampleResult = (sampleId: number, data: object) =>
  api.post(`/samples/${sampleId}/results`, data);

// Compliance
export const getComplianceSummary = () => api.get("/compliance/summary");
export const checkMissingSamples = (companyId?: number) =>
  api.post("/compliance/check-missing", companyId ? { company_id: companyId } : {});
export const recalculateCompliance = (companyId?: number) =>
  api.post("/compliance/recalculate", companyId ? { company_id: companyId } : {});

export const getViolations = (companyId?: number, year?: number) =>
  api.get("/compliance/violations", { params: { company_id: companyId, year } });
export const getViolationYears = (companyId?: number) =>
  api.get("/compliance/violations/years", { params: { company_id: companyId } });
export const getViolationHistory = (companyId: number) =>
  api.get(`/compliance/violations/${companyId}/history`);
export const getSamplingSchedule = (companyId?: number) =>
  api.get("/compliance/schedule", { params: { company_id: companyId } });
export const getSncReport = (params: { year?: number; half?: number; company_id?: number }) =>
  api.get("/compliance/snc", { params });

// Enforcement
export const getPendingEnforcement = () => api.get("/enforcement/pending");
export const getAllEnforcement = () => api.get("/enforcement/all");
// Send a submitted sample back to the industry to correct and re-certify (staff), and its re-certification (IU).
export const returnSample = (id: number, comment: string) => api.post(`/samples/${id}/return`, { comment });
export const resubmitSample = (id: number, data: { signer_name: string; password: string }) =>
  api.post(`/samples/${id}/resubmit`, data);
export const approveEnforcement = (id: number, data: object) =>
  api.post(`/enforcement/${id}/approve`, data);
export const overrideEnforcement = (id: number, data: object) =>
  api.post(`/enforcement/${id}/override`, data);
export const getEnforcementHistory = (companyId: number) =>
  api.get(`/enforcement/history/${companyId}`);

// ERG Configuration
export const getERGMatrix        = () => api.get("/erg-config/matrix");
export const updateERGMatrixEntry = (id: number, data: object) => api.put(`/erg-config/matrix/${id}`, data);
export const resetERGMatrix      = () => api.post("/erg-config/matrix/reset");
export const getERGFineSchedule  = () => api.get("/erg-config/fine-schedule");
export const updateERGFineSchedule = (id: number, data: object) => api.put(`/erg-config/fine-schedule/${id}`, data);

// Flow meters
export const getMeters = (companyId?: number) =>
  api.get("/admin/meters", { params: { company_id: companyId } });
export const createMeter = (data: object) => api.post("/admin/meters", data);
export const updateMeter = (id: number, data: object) => api.put(`/admin/meters/${id}`, data);

// Meter readings
export const getMeterReadings = (companyId?: number, archived = false) =>
  api.get("/admin/meters/readings", { params: { company_id: companyId, archived: archived || undefined } });
export const getLastMeterReading = (companyId?: number, meterType = "process", meterId?: number) =>
  api.get("/admin/meters/last-reading", { params: { company_id: companyId, meter_type: meterType, meter_id: meterId } });
export const createMeterReading = (data: object) =>
  api.post("/admin/meters/readings", data);
export const updateMeterReading = (id: number, data: object) =>
  api.put(`/admin/meters/readings/${id}`, data);
// Readings are archived, never deleted; an archived one can be restored.
export const archiveMeterReading = (id: number, reason: string) =>
  api.post(`/admin/meters/readings/${id}/archive`, { reason });
export const restoreMeterReading = (id: number) =>
  api.post(`/admin/meters/readings/${id}/restore`);

// Reports
export const getSampleReport = (params: object) =>
  api.get("/reports/samples", { params });
export const getMonthlyReport = (params: object) =>
  api.get("/reports/monthly", { params });
export const exportSampleReportExcel = (params: object) =>
  api.get("/reports/samples/export", { params, responseType: "blob" });
export const exportMonthlyReportPDF = (params: object) =>
  api.get("/reports/monthly/export", { params, responseType: "blob" });
export const getAnnualReport = (year?: number) =>
  api.get("/reports/pretreatment-annual", { params: year ? { year } : {} });
export const exportAnnualReportIcisXml = (year: number) =>
  api.get("/reports/pretreatment-annual/icis-xml", { params: { year }, responseType: "blob" });
export const exportAnnualReportIcisSubmission = (year: number) =>
  api.get("/reports/pretreatment-annual/icis-submission-xml", { params: { year }, responseType: "blob" });
// SCDES Pretreatment Performance Report: period "H1" | "H2" | "Y"; omit both for the last completed half.
export const getPpsReport = (year?: number, period?: string) =>
  api.get("/reports/pps", { params: year && period ? { year, period } : {} });
export const savePpsReport = (year: number, period: string, answers: object) =>
  api.put("/reports/pps", { year, period, answers });
export const downloadPpsReportPdf = (year: number, period: string) =>
  api.get("/reports/pps/pdf", { params: { year, period }, responseType: "blob" });
export const getSncPublication = (year: number) =>
  api.get("/reports/snc-publication", { params: { year } });
export const upsertSncPublication = (payload: {
  year: number; published_date: string; publication_name?: string; notes?: string;
}) => api.put("/reports/snc-publication", payload);
export const updateLocalLimits = (payload: {
  adoption_date?: string | null; evaluation_date?: string | null; parameter_codes?: string[];
}) => api.put("/reports/local-limits", payload);
export const upsertContamination = (payload: {
  year: number; discharge: boolean; discharge_text?: string;
  biosolids: boolean; biosolids_text?: string;
}) => api.put("/reports/contamination", payload);

// COA import
export const parseCOA = (companyId: number, file: File) => {
  const form = new FormData();
  form.append("company_id", String(companyId));
  form.append("file", file);
  return api.post("/samples/parse-coa", form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

export const parsePermit = (file: File) => {
  const form = new FormData();
  form.append("file", file);
  return api.post("/permits/parse-permit", form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

// Documents (uploaded PDFs — metadata in DB, bytes on the upload volume).
// entityType is one of: company | permit | potw_profile | sample | enforcement.
export const listDocuments = (entityType: string, entityId: number, includeArchived = false) =>
  api.get("/documents", { params: { entity_type: entityType, entity_id: entityId,
    ...(includeArchived ? { include_archived: 1 } : {}) } });
export const uploadDocument = (entityType: string, entityId: number, file: File, category?: string) => {
  const form = new FormData();
  form.append("entity_type", entityType);
  form.append("entity_id", String(entityId));
  if (category) form.append("category", category);
  form.append("file", file);
  return api.post("/documents", form, { headers: { "Content-Type": "multipart/form-data" } });
};
export const archiveDocument = (id: number) => api.delete(`/documents/${id}`);
// Keep the lab report (COA) PDF a sample's results were imported from.
export const attachLabReport = (sampleId: number, file: File) => {
  const form = new FormData();
  form.append("file", file);
  return api.post(`/samples/${sampleId}/lab-report`, form, { headers: { "Content-Type": "multipart/form-data" } });
};
// Direct link (cookie-authenticated) for download/preview in the browser.
export const documentDownloadUrl = (id: number) => `/api/documents/${id}/download`;

// CA inspections (40 CFR 403.8(f)(2)(v))
export const getInspections = (params?: { company_id?: number; year?: number; open_followups?: boolean }) =>
  api.get("/inspections", { params: {
    ...(params?.company_id ? { company_id: params.company_id } : {}),
    ...(params?.year ? { year: params.year } : {}),
    ...(params?.open_followups ? { open_followups: 1 } : {}),
  } });
export const createInspection = (data: object) => api.post("/inspections", data);
export const updateInspection = (id: number, data: object) => api.put(`/inspections/${id}`, data);
export const archiveInspection = (id: number) => api.delete(`/inspections/${id}`);
export const getInspectionAnnualStatus = (year?: number) =>
  api.get("/inspections/annual-status", { params: year ? { year } : {} });

// CA ↔ IU communication log (phone calls, emails, etc.)
export const getCommunications = (params?: { company_id?: number; year?: number; open_followups?: boolean }) =>
  api.get("/communications", { params: {
    ...(params?.company_id ? { company_id: params.company_id } : {}),
    ...(params?.year ? { year: params.year } : {}),
    ...(params?.open_followups ? { open_followups: 1 } : {}),
  } });
// People who may be named on a contact: CA staff, plus that IU's users.
export const getCommunicationPeople = (companyId?: number) =>
  api.get("/communications/people", { params: companyId ? { company_id: companyId } : {} });
export const createCommunication = (data: object) => api.post("/communications", data);
export const updateCommunication = (id: number, data: object) => api.put(`/communications/${id}`, data);
export const archiveCommunication = (id: number) => api.delete(`/communications/${id}`);

// In-app help assistant (how-to only)
export const getAssistantStatus = () => api.get("/assistant/status");
export const assistantChat = (messages: { role: "user" | "assistant"; content: string }[]) =>
  api.post("/assistant/chat", { messages });

// CA sampling events (40 CFR 403.8(f)(2)(v))
export const getCASamples = (params?: { company_id?: number; year?: number; open_followups?: boolean }) =>
  api.get("/ca-sampling", { params: {
    ...(params?.company_id ? { company_id: params.company_id } : {}),
    ...(params?.year ? { year: params.year } : {}),
    ...(params?.open_followups ? { open_followups: 1 } : {}),
  } });
export const createCASample = (data: object) => api.post("/ca-sampling", data);
export const updateCASample = (id: number, data: object) => api.put(`/ca-sampling/${id}`, data);
export const archiveCASample = (id: number) => api.delete(`/ca-sampling/${id}`);
export const getCASampleAnnualStatus = (year?: number) =>
  api.get("/ca-sampling/annual-status", { params: year ? { year } : {} });

// Surcharges
export const calculateSurcharge = (data: object) => api.post("/surcharges/calculate", data);
export const getSurcharges = (companyId?: number) =>
  api.get("/surcharges", { params: companyId ? { company_id: companyId } : {} });

// Monthly flow reports
export const getFlowReports = (companyId?: number) =>
  api.get("/flow-reports", { params: companyId ? { company_id: companyId } : {} });
export const getLastEndReading = (companyId?: number) =>
  api.get("/flow-reports/last-end-reading", { params: companyId ? { company_id: companyId } : {} });
export const createFlowReport = (data: object) => api.post("/flow-reports", data);
export const deleteFlowReport = (id: number) => api.delete(`/flow-reports/${id}`);
export const reviewFlowReport = (id: number, data: object) => api.post(`/flow-reports/${id}/review`, data);
export const rejectFlowReport = (id: number, comment: string) =>
  api.post(`/flow-reports/${id}/review`, { action: "reject", comment });

// Monthly submission certification (40 CFR 403.12(l))
export const getCertificationContext = (companyId?: number) =>
  api.get("/certifications/context", { params: companyId ? { company_id: companyId } : {} });
export const signCertification = (data: object) => api.post("/certifications", data);
// Transactional monthly submission: samples + optional flow report + certification in one atomic call.
export const submitMonthlySubmission = (data: object) => api.post("/monthly-submission", data);
export const getCertifications = (companyId?: number) =>
  api.get("/certifications", { params: companyId ? { company_id: companyId } : {} });

// Config
export const getPOTWConfig = () => api.get("/admin/config/potw");
export const getPotwProfile = () => api.get("/admin/config/potw/profile");
export const updatePotwProfile = (data: object) => api.put("/admin/config/potw/profile", data);
// The POTW's NPDES permits (renewed about every five years) and the renewal alert.
export const getNpdesPermits = () => api.get("/admin/npdes-permits");
export const addNpdesPermit = (data: object) => api.post("/admin/npdes-permits", data);
export const updateNpdesPermit = (id: number, data: object) => api.put(`/admin/npdes-permits/${id}`, data);
export const archiveNpdesPermit = (id: number) => api.post(`/admin/npdes-permits/${id}/archive`);
export const getParameters = () => api.get("/admin/parameters");
export const createParameter = (data: object) => api.post("/admin/parameters", data);
export const getFrequencies = () => api.get("/admin/frequencies");

// Audit log
export interface AuditFilter { user_id?: number; q?: string; date_from?: string; date_to?: string; }
export const getAuditLog = (limit = 100, offset = 0, filter: AuditFilter = {}) =>
  api.get("/admin/audit-log", { params: { limit, offset, ...filter } });

export default api;
